import {
  Box,
  Heading,
  Text,
  VStack,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Link as ChakraLink,
  Divider,
} from '@chakra-ui/react'
import { Link as RouterLink } from 'react-router-dom'
import HelpPageLayout from './HelpPageLayout'
import { useThemeColors } from '@/hooks/useThemeColors'
import { TroubleshootStep, type StepScreenshot, type ManualLink } from './HelpStepContent'

const H = (file: string) => `/assets/img/help/${file}`

type FaqItem = {
  question: string
  symptom: string
  causes: string[]
  steps: string[]
  screenshots?: StepScreenshot[]
  manualLinks?: ManualLink[]
}

type FaqCategory = {
  title: string
  intro?: string
  items: FaqItem[]
}

const faqCategories: FaqCategory[] = [
  {
    title: 'Dashboard (Operations home)',
    intro: 'Use this section when Operations home numbers look wrong or you cannot reach tickets/reports from the dashboard.',
    items: [
      {
        question: 'Dashboard numbers look empty, zero, or out of date',
        symptom: 'Action queue, pulse, or status cards show 0 / loading forever / an old Updated time.',
        causes: [
          'API stats call failed or is still loading.',
          'You are looking at the wrong environment or client context.',
          'Browser tab was idle; data has not refreshed yet.',
        ],
        steps: [
          'Note the Updated time under Operations home. Reload the page if it never advances past Loading….',
          'Confirm you can open Tickets or Incoming Request; if those also fail, check network or ask IT about API access.',
          'Compare with a teammate on the same role — permission or tenant mismatch can hide expected volumes.',
        ],
        screenshots: [
          {
            src: H('dash-01-operations-home.png'),
            alt: 'Operations home header',
            caption: 'Step 1: Check Updated time on Operations home.',
          },
        ],
        manualLinks: [{ label: 'Dashboard → Header and quick links' }],
      },
      {
        question: 'My Tickets opens the wrong list',
        symptom: 'Clicking My Tickets does not show only your assigned tickets.',
        causes: [
          'Ticket list view was changed after navigation.',
          'Filters on the Tickets page hide your rows.',
        ],
        steps: [
          'From Operations home, click My Tickets again (it sets the My view then opens /tickets).',
          'On Tickets, clear search and column filters, then confirm the My / All view control matches what you need.',
        ],
        screenshots: [
          {
            src: H('dash-01-operations-home.png'),
            alt: 'My Tickets quick link',
            caption: 'Step 1: Use the My Tickets quick link on Operations home.',
          },
        ],
        manualLinks: [{ label: 'Dashboard → Header and quick links' }],
      },
      {
        question: 'Report quick links or Task reports are missing',
        symptom: 'You cannot open SLA, Agent Performance, or Task reports from the dashboard.',
        causes: [
          'Your role does not include those report routes in navigation.',
          'The report page is not registered for your deployment yet.',
        ],
        steps: [
          'From Operations home, scroll to Reports and try All reports ↗ or a specific card.',
          'If the link 404s or redirects to dashboard, ask an administrator to grant report navigation / permissions.',
        ],
        screenshots: [
          {
            src: H('dash-05-charts-reports.png'),
            alt: 'Reports section',
            caption: 'Step 1: Reports section and All reports link on Operations home.',
          },
        ],
        manualLinks: [{ label: 'Dashboard → Charts, agent performance, and report shortcuts' }],
      },
      {
        question: 'Action queue or ticket status cards do not match the ticket list',
        symptom: 'Unresolved, Overdue, or Open counts on Operations home differ from what you see on Tickets.',
        causes: [
          'Queue cards are snapshot stats for Operations home. Ticket tabs apply your view (All, My, Unassigned) and filters.',
          'The Updated time is stale, or the API stats call failed.',
        ],
        steps: [
          'Note Updated under Operations home and reload if the cards stay on Loading.',
          'Use Action queue for unresolved, overdue, due today, and unassigned work. Click a card to open Tickets.',
          'Use Ticket status for Open, On Hold, and closed-family counts. Compare with a teammate if only your view looks empty.',
        ],
        screenshots: [
          {
            src: H('dash-02-action-queue-pulse.png'),
            alt: 'Action queue and pulse',
            caption: 'Action queue and Today’s pulse on Operations home.',
          },
          {
            src: H('dash-03-ticket-status.png'),
            alt: 'Ticket status strip',
            caption: 'Ticket status area — Open and hold buckets, separate from the Tickets tab filters.',
          },
        ],
        manualLinks: [{ label: 'Dashboard → Suggested process flow' }],
      },
    ],
  },
  {
    title: 'Case Threads — email by client / requestor',
    intro:
      'Use this section when helpdesk mail should be worked as a Ticket Request conversation (not a flat mailbox). Case Threads never creates a parent ticket or task by itself.',
    items: [
      {
        question: 'Where do I work customer email conversations?',
        symptom: 'Mail arrived and you need the full thread for a client or requestor, not only the Incoming Request row.',
        causes: [
          'Case Threads groups mail by Client or Requestor, then by Ticket Request, then the conversation.',
          'Mailbox Inbox is a flat reader; Incoming Request is for acknowledge → parent ticket.',
        ],
        steps: [
          'Open Case Threads from the sidebar, or use the header mail icon (Unread case threads).',
          'Choose Clients or Requestors. Select a party on the left, then a Ticket Request in the middle.',
          'Read Customer / Agent / Internal note / System messages on the right. Use Reply, Mark waiting, or Open parent when a parent exists.',
        ],
        screenshots: [
          {
            src: H('ct-01-overview.png'),
            alt: 'Case Threads overview',
            caption: 'Case Threads three columns: parties, ticket requests, and the communication thread.',
          },
          {
            src: H('ct-04-thread.png'),
            alt: 'Communication pane actions',
            caption: 'Thread header: Reply, Mark waiting, Open parent (or No parent ticket yet).',
          },
        ],
        manualLinks: [
          { label: 'Case Threads → Layout, Clients / Requestors toggle, and header shortcut' },
          { label: 'Case Threads → Communication pane — thread, message types, and actions' },
        ],
      },
      {
        question: 'Email is stuck in Unlinked or Needs review',
        symptom: 'A message appears under Unlinked or Needs review and is not on a Ticket Request yet.',
        causes: [
          'Unlinked means no reliable client match. Needs review means the matcher is unsure (ambiguous / medium confidence).',
          'These queues need Create Ticket Request, Attach to existing, or Ignore.',
        ],
        steps: [
          'Open Unlinked or Needs review on the left. Select the email in the middle column.',
          'If a Suggested REQ-… badge looks correct, use Attach to existing and confirm that request (search by request number, subject, or contact).',
          'If it is new work, use Create Ticket Request, pick the Client (name or code), and confirm. This creates a Ticket Request only — acknowledge later on Incoming Request.',
          'Use Ignore only when the mail should leave the queue without creating or attaching work.',
        ],
        screenshots: [
          {
            src: H('ct-06-needs-review.png'),
            alt: 'Needs review triage',
            caption: 'Needs review email with Suggested REQ badge and Create / Attach / Ignore.',
          },
          {
            src: H('ct-07-unlinked.png'),
            alt: 'Unlinked triage',
            caption: 'Unlinked email with reason badge and the same triage actions.',
          },
          {
            src: H('ct-08-attach-modal.png'),
            alt: 'Attach modal',
            caption: 'Attach to existing Ticket Request — search and Attach.',
          },
          {
            src: H('ct-09-create-modal.png'),
            alt: 'Create Ticket Request modal',
            caption: 'Create Ticket Request — client search; parent ticket is not created here.',
          },
        ],
        manualLinks: [{ label: 'Case Threads → Queue triage — Create, Attach, Ignore' }],
      },
      {
        question: 'I cannot change the reply subject on Case Threads',
        symptom: 'Subject field in the Reply drawer is read-only or not what you typed.',
        causes: [
          'Subject is locked to the Ticket Request display subject so the conversation stays on one case.',
        ],
        steps: [
          'Open the request → Reply. Confirm Subject (locked to this request) shows the locked Re: subject.',
          'Write the body, optional Cc, and optionally Reply all (uses saved To and Cc).',
          'Choose Reply to email the customer, or Internal note for a private timeline note.',
        ],
        screenshots: [
          {
            src: H('ct-05-reply-drawer.png'),
            alt: 'Reply drawer locked subject',
            caption: 'Reply drawer: locked subject, body, Cc, Reply all, Reply, Internal note.',
          },
        ],
        manualLinks: [{ label: 'Case Threads → Reply drawer — fields' }],
      },
      {
        question: 'Mark waiting / Needs attention — what do they mean?',
        symptom: 'You used Mark waiting, or a request shows Needs attention in the middle column.',
        causes: [
          'Mark waiting sets the Ticket Request to waiting on the customer (Pending) and writes a system note.',
          'Needs attention appears when needs_human_review is true (for example a customer reply while waiting, or a match still needing review).',
        ],
        steps: [
          'On an open request thread, click Mark waiting when you are blocked on the customer.',
          'When Needs attention appears on a request row, open the thread and reply or clear the review reason.',
          'Parent status (when a parent exists) still wins in the status badge; Open parent jumps to the ticket view.',
        ],
        screenshots: [
          {
            src: H('ct-03-request-list.png'),
            alt: 'Needs attention badge',
            caption: 'Middle column: request rows with status and Needs attention when review is required.',
          },
          {
            src: H('ct-04-thread.png'),
            alt: 'Mark waiting on thread',
            caption: 'Thread actions include Mark waiting next to Reply and Open parent.',
          },
        ],
        manualLinks: [
          { label: 'Case Threads → Middle column — Ticket Requests and queue emails' },
          { label: 'Case Threads → Communication pane — thread, message types, and actions' },
        ],
      },
      {
        question: 'Case Threads created a Ticket Request but there is still no ticket',
        symptom: 'Create Ticket Request succeeded (or intake auto-created a TR) but Tickets has no parent.',
        causes: [
          'By design, Case Threads / email intake create a Ticket Request only — never a parent ticket or task.',
          'Parent creation happens on Incoming Request acknowledge (Also create parent ticket) or Create Ticket.',
        ],
        steps: [
          'Find the REQ-… on Incoming Request (All Request or Acknowledge).',
          'Use Save & Acknowledge with Also create parent ticket, or Create Ticket on an acknowledged row.',
          'Return to Case Threads → Open parent once the parent exists.',
        ],
        screenshots: [
          {
            src: H('ct-09-create-modal.png'),
            alt: 'Create TR only reminder',
            caption: 'Create modal states that only a Ticket Request is created here.',
          },
          {
            src: H('ir-area-drawer.png'),
            alt: 'Acknowledge with parent',
            caption: 'Incoming Request drawer: Also create parent ticket, then Save & Acknowledge.',
          },
        ],
        manualLinks: [
          { label: 'Case Threads → How Case Threads fits Incoming Request and Mailbox Inbox' },
          { label: 'Incoming Request → End-to-end process flow' },
        ],
      },
      {
        question: 'Customer emailed after a case was closed',
        symptom:
          'A reply or new message about a closed Ticket Request appeared on the thread or in Needs review.',
        causes: [
          'Within 14 days of close, intake can attach the mail and reopen the Ticket Request (Needs attention). Acknowledge SLA is not restarted from scratch.',
          'After 14 days, the message goes to Needs review so the team can Attach (reopen related) or Create Ticket Request for a new case.',
        ],
        steps: [
          'Open Case Threads and check the client/requestor thread first.',
          'If the email is in Needs review, use Attach to existing or Create Ticket Request — do not Ignore unless it is noise.',
          'Continue the conversation on the Ticket Request; acknowledge / parent work still goes through Incoming Request when needed.',
        ],
        screenshots: [
          {
            src: H('ct-06-needs-review.png'),
            alt: 'Needs review after closed case',
            caption: 'Closed-outside-window mail lands in Needs review for Attach or Create Ticket Request.',
          },
          {
            src: H('ct-04-thread.png'),
            alt: 'Reopened case thread',
            caption: 'Within the reopen window, the reply stays on the same Ticket Request conversation.',
          },
        ],
        manualLinks: [
          { label: 'Case Threads → How Case Threads fits Incoming Request and Mailbox Inbox' },
          { label: 'Case Threads → Queue triage — Create, Attach, Ignore' },
        ],
      },
    ],
  },
  {
    title: 'From email to closing a ticket',
    intro:
      'Walk this path when a request starts as email (or portal / voice) and must end as a closed parent ticket. Use Case Threads for the conversation, Incoming Request to acknowledge, then Tickets to close. Each answer uses a screenshot of that area only.',
    items: [
      {
        question: 'A customer email is not showing as a ticket yet',
        symptom: 'Mail arrived (or the customer says they wrote) but Tickets has no matching parent.',
        causes: [
          'Email intake creates a Ticket Request first (often visible in Case Threads). It is not a ticket until someone acknowledges it and a parent is created.',
          'The email may still be in Case Threads → Unlinked or Needs review.',
          'The Incoming Request row is on Acknowledge (already acked, no parent), Trash, or hidden by Search / From / To.',
        ],
        steps: [
          'Open Case Threads. Check the client/requestor and the Ticket Request thread, or clear Unlinked / Needs review first.',
          'Open Incoming Request. On Per Source, Email is one of the intake channels (with Portal and Voice). Top stats are overall volume — From / To do not change those cards.',
          'On All Request, find the row by Request ID, Subject, or the Email source badge. Click Subject to open Review request. Pending rows show a live Timer. If the row is gone, check Acknowledge, then Trash.',
        ],
        screenshots: [
          {
            src: H('ct-01-overview.png'),
            alt: 'Case Threads overview',
            caption: 'Case Threads — confirm the email landed on a Ticket Request or a triage queue.',
          },
          {
            src: H('ir-area-stats.png'),
            alt: 'Incoming Request stat cards',
            caption: 'Stats area: Total Open / Pending / Requests, ack KPIs, and volume Per Source (Email, Portal, Voice).',
          },
          {
            src: H('ir-area-queue.png'),
            alt: 'All Request table',
            caption: 'Queue area: Source badge (Email), Subject, Assigned to, Timer.',
          },
        ],
        manualLinks: [
          { label: 'Case Threads → Layout, Clients / Requestors toggle, and header shortcut' },
          { label: 'Incoming Request → Stats overview and Available Agents' },
          { label: 'Incoming Request → Table columns by tab' },
        ],
      },
      {
        question: 'What do I edit and email before I acknowledge?',
        symptom: 'The request is Pending and you need to correct contact details or reply to the requestor first.',
        causes: [
          'Enriched fields stay editable only while the request is Pending.',
          'Compose Email sends from the helpdesk mailbox to the contact on the request. It does not create the ticket.',
        ],
        steps: [
          'Click Subject. Confirm Source is Email (or the real channel), client, submitted time, and contact.',
          'Edit Subject, Description, Contact name, Email, Phone, Priority, and Review notes if they are wrong. Activity at the bottom shows intake, for example “Intake created from email”.',
          'Use Compose Email to reply: From (helpdesk), To (contact), optional Cc, Subject (often Re: Request …), Message, then Send. Call requestor (ALICE AI or Vicidial) is the phone path, not email.',
          'Leave Also create parent ticket checked if the next step should open the new ticket editor. Uncheck it if you only want to stop the timer and create the ticket later.',
        ],
        screenshots: [
          {
            src: H('ir-area-drawer.png'),
            alt: 'Review request drawer',
            caption:
              'Drawer: source Email, enriched fields, intake activity, Also create parent ticket, Save & Acknowledge.',
          },
          {
            src: H('ir-06-compose-email.png'),
            alt: 'Compose Email modal',
            caption: 'Compose Email: From helpdesk, To contact, Cc, Subject, Message, Send.',
          },
        ],
        manualLinks: [
          { label: 'Incoming Request → Triage drawer — fields and actions' },
          { label: 'Incoming Request → Compose Email modal' },
        ],
      },
      {
        question: 'Acknowledge did not open a ticket, or it said the ticket was already created',
        symptom: 'After Save & Acknowledge you stayed on the queue, or a second attempt says the request was already acknowledged.',
        causes: [
          'Also create parent ticket was unchecked, so the row moves to Acknowledge without a parent.',
          'Another agent acknowledged first. Creating a parent is a separate Create Ticket step when no parent exists.',
          'The confirm dialog’s button is Confirm & Acknowledge. The checkbox itself sits on the drawer, not inside the dialog.',
        ],
        steps: [
          'On the drawer, set Also create parent ticket, then Save & Acknowledge. Confirm & Acknowledge stops the timer. With the box checked, you should land on the new parent’s Edit page.',
          'If you only acknowledged, open the Acknowledge tab and use Create Ticket (green) to open Create New Ticket with the request linked.',
          'Do not acknowledge again when a parent ticket number already exists — open that ticket from Tickets.',
        ],
        screenshots: [
          {
            src: H('ir-area-drawer.png'),
            alt: 'Also create parent ticket on the drawer',
            caption: 'Checkbox Also create parent ticket is on the drawer footer, default on.',
          },
          {
            src: H('ir-04-acknowledge-modal.png'),
            alt: 'Confirm and Acknowledge dialog',
            caption: 'Confirm dialog: Cancel and Confirm & Acknowledge. It does not contain the checkbox.',
          },
          {
            src: H('ir-07-acknowledge-tab.png'),
            alt: 'Acknowledge tab',
            caption: 'Acknowledge tab — Create Ticket when the request is acked and has no parent yet.',
          },
        ],
        manualLinks: [{ label: 'Incoming Request → End-to-end process flow' }],
      },
      {
        question: 'How do I submit the parent ticket after the email is linked?',
        symptom: 'You are on Create or Edit and need to know which areas to complete before the ticket is live.',
        causes: [
          'Acknowledge-with-create opens Edit. Create Ticket from the Acknowledge tab opens Create New Ticket already linked.',
          'A blank Create means the request was not passed in (refresh or a new tab). Link it again from the Request panel.',
          'Submit label and whether an FE Visit is created depend on Dispatch readiness.',
        ],
        steps: [
          'Request panel: confirm the linked request (Search request…, Add request / Edit request, Details).',
          'Complete Client & billing, Ticket details (Subject, Source, Severity, description), classification, and location. Assign an agent if your role allows it.',
          'Field engineer dispatch appears only with Requires field engineer dispatch and a client. Pending submits the parent with no FE Visit. On Hold needs Hold reason, Expected follow-up, and optional Remarks. Ready needs a site pin and Primary FE before Submit Ticket & Create FE Visit.',
          'Save as draft to keep editing, or use the submit button. A successful submit opens the ticket view.',
        ],
        screenshots: [
          {
            src: H('tkt-form-request.png'),
            alt: 'Request panel',
            caption: 'Request area — link the acknowledged email request if the form did not open prefilled.',
          },
          {
            src: H('tkt-form-dispatch.png'),
            alt: 'Dispatch readiness Pending',
            caption: 'Dispatch area — Pending submits the parent without creating an FE Visit.',
          },
          {
            src: H('tkt-form-submit.png'),
            alt: 'Save as draft and Submit',
            caption: 'Footer area — Cancel, Save as draft, and the submit action (wording changes with readiness).',
          },
        ],
        manualLinks: [
          { label: 'Tickets → Create / Edit form — Request, Client & billing, Ticket details' },
          { label: 'Tickets → Field engineer dispatch and submit actions' },
        ],
      },
      {
        question: 'Which tasks must be finished before the ticket can close cleanly?',
        symptom: 'The ticket is open, work is split across tasks, and you are not sure what you can complete from Support Admin.',
        causes: [
          'Tasks on the ticket are separate from the parent status. Closing the parent does not complete those tasks.',
          'Field Visit is completed on the mobile app. Parts procurement stays open until parts are received or marked not available.',
          'A dependency can block Mark complete until the blocker task is done.',
        ],
        steps: [
          'On the ticket, open the Tasks tab. Use List to see every row: complete control, title, assignee, SLA, and View task.',
          'Use filters All / Active / Blocked / Breached / Completed. Create task, Apply template, and Reset workflow are on this toolbar.',
          'View task opens the drawer. Generic tasks: Start, Mark complete, Skip. Field Visit: Reassign, Cancel visit, or Force close — not list complete. Parts: finish the Parts tab first.',
          'Document collection and other generic tasks can be completed here when the checklist and dependencies are clear.',
        ],
        screenshots: [
          {
            src: H('tkt-area-tasks.png'),
            alt: 'Tasks list',
            caption:
              'Tasks area: Create task, filters, CSO Review, Field engineer visit (blocked), Parts, and a completed Document collection.',
          },
          {
            src: H('tkt-view-task-drawer.png'),
            alt: 'Task drawer',
            caption: 'Task drawer area — status, assignee, and Start / Mark complete / Skip for a generic task.',
          },
        ],
        manualLinks: [{ label: 'Tickets → Tasks tab — list, create, templates, and task drawer' }],
      },
      {
        question: 'How do I close the ticket and email the CSAT survey?',
        symptom: 'Work looks done and you need the parent Closed, a customer-visible note, or the survey email.',
        causes: [
          'Status is changed on the view page Properties panel, not on the Incoming Request.',
          'Closed, Resolved incomplete, and Cancelled run a close check. Open tasks warn you; they are not auto-closed.',
          'Send CSAT survey appears only after a close-family status that is eligible (Closed or Resolved incomplete), and it emails the survey.',
        ],
        steps: [
          'Open the ticket view → Overview → Properties. Status is a dropdown (Open, On Hold, Closed, and any other catalog statuses). While the ticket is open, CSAT shows Available once closed.',
          'Choose Closed. If tasks are still open, the warning lists them (task number, title, status). Show/Hide open tasks, then Close ticket anyway or Cancel. Cancel restores the previous status.',
          'Close ticket anyway saves Closed and writes a system activity line. It does not complete the open tasks — finish or skip those separately if they should not stay open.',
          'After the ticket is Closed or Resolved incomplete, use Send CSAT survey on Properties. The toast names the recipient email.',
          'Customer-visible updates and internal notes are on Activity & Comments, not on the task drawer. Check Internal note so the customer does not see the post. System rows (status changes) show under All and System.',
        ],
        screenshots: [
          {
            src: H('tkt-view-properties.png'),
            alt: 'Ticket properties while open',
            caption: 'Properties area: Status Open, Source email, CSAT locked with Available once closed.',
          },
          {
            src: H('tkt-view-close-blocked.png'),
            alt: 'Close blocked by open tasks',
            caption:
              'Close warning: open tasks stay open. Close ticket anyway or Cancel.',
          },
          {
            src: H('tkt-area-activity.png'),
            alt: 'Activity and comments composer',
            caption:
              'Activity area: All / Comments / System, thread and status-change row, Write a comment, Internal note, Post.',
          },
        ],
        manualLinks: [
          { label: 'Tickets → View ticket — Overview' },
          { label: 'Tickets → Activity & Comments' },
        ],
      },
    ],
  },
  {
    title: 'Incoming Request — acknowledge and create ticket',
    intro: 'Primary recovery path when intake acknowledge or ticket creation does not behave as expected.',
    items: [
      {
        question: 'Save & Acknowledge fails or says already acknowledged',
        symptom: 'Acknowledge button spins then errors, or toast says the request was already acknowledged.',
        causes: [
          'Another agent acknowledged the same request first.',
          'Network or validation error on the acknowledge API.',
          'You acknowledged from the drawer and again from the row action.',
        ],
        steps: [
          'Reload the Incoming Request table (or wait for the ~30s auto-refresh).',
          'Switch to the Acknowledge tab and search for the Request ID.',
          'If a parent ticket number already exists, open it from Tickets instead of acknowledging again.',
          'If acknowledge failed with a different message, open the triage drawer, fix required enriched fields, then try Save & Acknowledge again.',
        ],
        screenshots: [
          {
            src: H('ir-area-drawer.png'),
            alt: 'Drawer Also create parent ticket',
            caption: 'Checkbox Also create parent ticket is on the drawer, then Save & Acknowledge.',
          },
          {
            src: H('ir-07-acknowledge-tab.png'),
            alt: 'Acknowledge tab',
            caption: 'Step 2: Acknowledge tab — find the request after refresh.',
          },
        ],
        manualLinks: [
          { label: 'Incoming Request → Row actions' },
          { label: 'Incoming Request → Triage drawer' },
        ],
      },
      {
        question: 'Parent ticket was not created after acknowledge',
        symptom: 'Request is acknowledged but there is no parent ticket / Create Ticket is still required.',
        causes: [
          'Also create parent ticket was unchecked on the confirm modal.',
          'Ticket create returned a warning; request was still acknowledged.',
        ],
        steps: [
          'Open the Acknowledge tab and locate the request.',
          'Use the green Create Ticket action (or Create Ticket in the drawer) to open /tickets/create with the request prefilled.',
          'Next time, leave Also create parent ticket checked if you want navigate-to-edit in one step.',
        ],
        screenshots: [
          {
            src: H('ir-area-drawer.png'),
            alt: 'Also create parent ticket checkbox',
            caption: 'Leave Also create parent ticket checked on the drawer if you want the parent in the same step.',
          },
          {
            src: H('ir-07-acknowledge-tab.png'),
            alt: 'Create Ticket on Acknowledge tab',
            caption: 'Step 2: Create Ticket when acknowledged without a parent.',
          },
        ],
        manualLinks: [{ label: 'Incoming Request → End-to-end process flow' }],
      },
      {
        question: 'Timer or Ack SLA shows late / unexpected',
        symptom: 'Timer shows late acknowledgement or Ack SLA badge is orange (N min late).',
        causes: [
          'Acknowledge completed after the acknowledge due time.',
          'You are reading an already-acknowledged row’s historical delay.',
        ],
        steps: [
          'On All Request, use Timer to see live countdown vs due time before acknowledging.',
          'On Acknowledge, Ack SLA shows On time or N min late based on acknowledge_delay_minutes.',
          'Prioritize oldest pending rows (and high-priority enriched requests) to improve on-time rate.',
        ],
        screenshots: [
          {
            src: H('ir-area-queue.png'),
            alt: 'Timer column',
            caption: 'All Request area: Timer on pending rows (email, portal, voice).',
          },
        ],
        manualLinks: [{ label: 'Incoming Request → Table columns by tab' }],
      },
      {
        question: 'I cannot edit fields in the triage drawer',
        symptom: 'Subject, Description, contact fields, Priority, or Review notes are disabled.',
        causes: [
          'The request is already acknowledged or already has a parent ticket.',
          'You opened the request from the Trash tab (read-only).',
        ],
        steps: [
          'Confirm the status badge is Pending (not Acknowledged / Trashed).',
          'If already acknowledged, edit happens on the parent ticket — use Create Ticket or open the linked ticket.',
          'If in Trash, Restore first, then open Subject again from All Request.',
        ],
        screenshots: [
          {
            src: H('ir-area-drawer.png'),
            alt: 'Triage drawer fields',
            caption: 'Enriched request area — editable only while the badge is Pending.',
          },
        ],
        manualLinks: [{ label: 'Incoming Request → Triage drawer — fields and actions' }],
      },
    ],
  },
  {
    title: 'Incoming Request — assign, email, call, trash',
    intro: 'Role-gated and communication actions on the intake queue.',
    items: [
      {
        question: 'Available Agents panel or Assign menu is missing',
        symptom: 'No Available Agents column on the right, or Assigned to has no assign button.',
        causes: [
          'Your role cannot assign support agents (panel and Assign menu are assigner-only).',
          'The row already has a parent ticket (assign cell shows dash).',
        ],
        steps: [
          'Ask a team lead to assign, or request the assigner role if that is your job.',
          'Open Agent Status from elsewhere if you only need to view agent load (View All is on the Available Agents card for assigners).',
        ],
        screenshots: [
          {
            src: H('ir-09-available-agents.png'),
            alt: 'Available Agents',
            caption: 'Available Agents appears only for assigner roles.',
          },
        ],
        manualLinks: [{ label: 'Incoming Request → Stats overview and Available Agents' }],
      },
      {
        question: 'Compose Email or Call requestor is unavailable',
        symptom: 'Mail / call actions are missing in the row or drawer.',
        causes: [
          'You are on the Trash tab (communication actions are disabled).',
          'Call / mailbox integration is not configured for your environment.',
        ],
        steps: [
          'Switch to All Request or Acknowledge and open the request again.',
          'Use Compose Email for Cc / Subject / body / Send; use Call requestor for ALICE AI or Vicidial when offered.',
          'If Call options error after confirm, capture the toast message and escalate to IT / telephony admins.',
        ],
        screenshots: [
          {
            src: H('ir-06-compose-email.png'),
            alt: 'Compose Email modal',
            caption: 'Compose Email: From, To, Cc, Subject, body, Send.',
          },
          {
            src: H('ir-05-triage-drawer.png'),
            alt: 'Call requestor in drawer',
            caption: 'Drawer communication: Compose Email and Call requestor.',
          },
        ],
        manualLinks: [
          { label: 'Incoming Request → Compose Email modal' },
          { label: 'Incoming Request → Triage drawer — fields and actions' },
        ],
      },
      {
        question: 'Request disappeared from All Request',
        symptom: 'A row that was pending is gone from the All Request tab.',
        causes: [
          'It was acknowledged (moved to Acknowledge tab).',
          'It was soft-deleted (Trash tab).',
          'Search or From/To filters hide it.',
        ],
        steps: [
          'Clear Search and From / To dates.',
          'Check the Acknowledge tab for the Request ID.',
          'Check Trash and Restore if it was deleted by mistake — do not Delete Permanently unless required.',
        ],
        screenshots: [
          {
            src: H('ir-03-filters.png'),
            alt: 'Tabs and filters',
            caption: 'Step 1–2: Clear filters; check Acknowledge vs Trash tabs.',
          },
          {
            src: H('ir-08-trash-tab.png'),
            alt: 'Trash tab',
            caption: 'Step 3: Trash — Restore or Delete Permanently.',
          },
        ],
        manualLinks: [
          { label: 'Incoming Request → Tabs, search, and date filters' },
          { label: 'Incoming Request → Trash and restore' },
        ],
      },
      {
        question: 'Date filter changed the top stats (unexpected)',
        symptom: 'You expected Total Open / KPIs to follow From and To, but they did not.',
        causes: [
          'By design, Incoming stats are loaded without date filtering; From/To apply only to the table query.',
        ],
        steps: [
          'Use From / To to narrow the grid only.',
          'Read Total Open, Total Pending, Total Requests, and ack KPIs as overall incoming stats.',
          'Use volume-per-client / type / source cards for mix; use reports for dated analytics.',
        ],
        screenshots: [
          {
            src: H('ir-area-stats.png'),
            alt: 'Incoming stats cards',
            caption: 'Stats area only — these cards ignore the From / To dates on the queue.',
          },
        ],
        manualLinks: [{ label: 'Incoming Request → Stats overview and Available Agents' }],
      },
    ],
  },
  {
    title: 'Tickets — list, create, edit, view',
    intro: 'Use this section when the Tickets list, create/edit form, or view page does not behave as expected.',
    items: [
      {
        question: 'My Tickets / Unassigned / My team tab is missing or wrong',
        symptom: 'You do not see the tab you expect, or My Tickets shows someone else’s work.',
        causes: [
          'My team is Senior Team Lead only; Unassigned is assigner roles only.',
          'The remembered ticket view tab differs from what you expect.',
          'Dashboard My Tickets sets the My view before opening /tickets.',
        ],
        steps: [
          'Open Tickets and check which tab is selected (All Tickets, My Tickets, My team, Unassigned, Trash).',
          'If Unassigned or My team is missing, confirm your role with an administrator.',
          'Use All Tickets and clear search / Client filter if a row seems missing.',
        ],
        screenshots: [
          {
            src: H('tkt-01-list-all.png'),
            alt: 'Tickets tabs',
            caption: 'Step 1: Tickets list tabs and Create New Ticket.',
          },
        ],
        manualLinks: [{ label: 'Tickets → List page — tabs, search, columns, and Create' }],
      },
      {
        question: 'Cannot edit a submitted ticket from the list',
        symptom: 'Edit is missing; only View Details appears.',
        causes: [
          'Submitted tickets are view-first from the list; drafts keep Edit.',
        ],
        steps: [
          'Open View Details on the submitted ticket.',
          'Click Edit ticket on the view hero to open the edit form.',
          'Use Save as draft / Submit according to dispatch readiness rules.',
        ],
        screenshots: [
          {
            src: H('tkt-07-view-overview.png'),
            alt: 'Ticket view Edit ticket',
            caption: 'Step 2: Edit ticket from the view page.',
          },
        ],
        manualLinks: [
          { label: 'Tickets → Row actions, assign, trash, and open edit/view' },
          { label: 'Tickets → View ticket — Overview' },
        ],
      },
      {
        question: 'Submit ticket is disabled or FE Visit will not create',
        symptom: 'Submit Ticket & Create FE Visit stays disabled, or Ready dispatch will not submit.',
        causes: [
          'Dispatch readiness is Ready but site pin or Primary FE is incomplete.',
          'Invalid FE assignments need reassign / remove.',
          'Client is missing so dispatch cannot be configured.',
        ],
        steps: [
          'Confirm Requires field engineer dispatch and a Client are set.',
          'Set Dispatch readiness to Pending (submit without FE Visit), On Hold (hold fields required), or Ready.',
          'For Ready: Confirm location and find engineers, assign Primary FE, resolve invalid assignments, then submit.',
          'Check the context rail checklist for incomplete required items.',
        ],
        screenshots: [
          {
            src: H('tkt-form-dispatch-on-hold.png'),
            alt: 'Dispatch On Hold fields',
            caption: 'On Hold: Hold reason, Expected follow-up, Remarks.',
          },
          {
            src: H('tkt-form-dispatch-ready.png'),
            alt: 'Dispatch Ready site pin',
            caption: 'Ready: confirm site pin, then assign Primary FE before Create FE Visit.',
          },
        ],
        manualLinks: [{ label: 'Tickets → Field engineer dispatch and submit actions' }],
      },
      {
        question: 'Cannot close the ticket because tasks are still open',
        symptom: 'Choosing Closed (or Resolved incomplete / Cancelled) shows a warning and the status does not save until you choose.',
        causes: [
          'Close validation found tasks that are not complete or skipped.',
          'Closing the parent does not close those tasks.',
        ],
        steps: [
          'Read the warning on Properties: “N tasks are still open. Closing this ticket will not close them.”',
          'Use Show open tasks for the task number, title, and status.',
          'Cancel to stay on the current status and finish the tasks (Tasks tab).',
          'Choose Close ticket anyway only when the parent should close and the leftover tasks are acceptable.',
          'After Closed or Resolved incomplete, Send CSAT survey emails the survey. It stays “Available once closed” until then.',
        ],
        screenshots: [
          {
            src: H('tkt-view-properties.png'),
            alt: 'Status dropdown before close',
            caption: 'Properties: change Status here. CSAT is locked until the ticket is closed.',
          },
          {
            src: H('tkt-view-close-blocked.png'),
            alt: 'Open tasks close warning',
            caption: 'Warning lists open tasks. Close ticket anyway, or Cancel.',
          },
        ],
        manualLinks: [{ label: 'Tickets → View ticket — Overview' }],
      },
      {
        question: 'Cannot complete a task from the Tasks list',
        symptom: 'Complete control is disabled, or Mark complete fails / stays blocked.',
        causes: [
          'Field Visit tasks are completed from the mobile app lifecycle — not from the list.',
          'Parts procurement still has linked parts that are not received / not_available.',
          'Task is blocked by a dependency, skipped, or already completed.',
          'Completion preflight fails (checklist / dependencies / parts) inside the drawer.',
        ],
        steps: [
          'Open View task and check type badge (Field Visit vs Parts Procurement vs generic).',
          'For Parts: receive or mark parts not available, then Mark complete in the drawer.',
          'For Field Visit: use Reassign / Cancel visit / Force close as needed; complete on mobile.',
          'Clear Blocked dependencies or finish blocker tasks first.',
        ],
        screenshots: [
          {
            src: H('tkt-area-tasks.png'),
            alt: 'Tasks list',
            caption: 'Tasks area — complete control, type, and View task. Field Visit stays open until mobile (or Force close).',
          },
          {
            src: H('tkt-view-task-drawer.png'),
            alt: 'Task drawer actions',
            caption: 'Step 2: Drawer actions — Start / Mark complete / Skip.',
          },
        ],
        manualLinks: [{ label: 'Tickets → Tasks tab — list, create, templates, and task drawer' }],
      },
      {
        question: 'Comment posted but customer should not see it / need internal note',
        symptom: 'Need a private note on the ticket, or replies are missing.',
        causes: [
          'Internal note checkbox was left unchecked (posts as a customer-visible comment).',
          'Looking at task Activity instead of ticket Activity & Comments (separate feeds).',
        ],
        steps: [
          'Open Activity & Comments.',
          'Check Internal note (not visible to customer), write the note, Post (or Ctrl/Cmd+Enter).',
          'Use Reply under a comment for threaded replies; edit your own posts via the pencil.',
          'Filter System to see status-change activity without comment noise.',
        ],
        screenshots: [
          {
            src: H('tkt-area-activity.png'),
            alt: 'Activity and Comments',
            caption: 'Activity area: filters, thread, composer, Internal note, Post.',
          },
        ],
        manualLinks: [{ label: 'Tickets → Activity & Comments' }],
      },
      {
        question: 'Create from Incoming did not prefill / wrong landing page',
        symptom: 'Create New Ticket is empty after Incoming, or you expected Edit but got Create (or the reverse).',
        causes: [
          'Acknowledge with Also create parent ticket navigates to Edit of the new parent.',
          'Create Ticket from Acknowledge / drawer opens Create hydrated from fromRequest.',
          'Hydration state was lost (refresh / new tab).',
        ],
        steps: [
          'From Incoming, use Create Ticket on an acknowledged request without a parent — you should land on Create with the request linked.',
          'If you used Also create parent ticket, open the ticket from Tickets list (or the Edit URL you were sent to) instead of starting a blank Create.',
          'If the request is not linked, use Search request... or Add request on the form.',
        ],
        screenshots: [
          {
            src: H('tkt-form-request.png'),
            alt: 'Create New Ticket Request panel',
            caption: 'Step 3: Link request on Create New Ticket if hydration was lost.',
          },
        ],
        manualLinks: [{ label: 'Tickets → Process flows (Incoming → Ticket and day-to-day)' }],
      },
      {
        question: 'Cannot assign agent on list or form',
        symptom: 'No Assign to control, or agent search is read-only.',
        causes: [
          'Your role cannot assign/reassign support agents.',
          'You are on My Tickets (Agent column hidden) or a non-assigner role.',
        ],
        steps: [
          'Assigners: use Unassigned tab or Agent column Assign to (Unassign / Assign to me / agent).',
          'On the form, use Assign to me or agent search when your role allows reassignment.',
          'Ask a Team Lead / Senior Team Lead / Web Admin / Developer Account to reassign if needed.',
        ],
        screenshots: [
          {
            src: H('tkt-03-list-unassigned.png'),
            alt: 'Assign to menu',
            caption: 'Step 1: Assign to on Unassigned (assigner roles).',
          },
        ],
        manualLinks: [{ label: 'Tickets → Row actions, assign, trash, and open edit/view' }],
      },
    ],
  },
  {
    title: 'Agent Status, My Tasks, and Mailbox',
    intro:
      'Assignment load, your cross-ticket task list, and the flat Mailbox Inbox. Prefer Case Threads for case-centered email; these pages are separate from Incoming Request triage.',
    items: [
      {
        question: 'I cannot change an agent’s Online / Break / Lunch / Offline status',
        symptom: 'User Status is plain text, or Agent Status is missing from the sidebar.',
        causes: [
          'Only roles that can assign support agents get the status dropdown. Everyone else sees a read-only label.',
          'Team leads see people who report to them. Other assigners see the assignable-agent list.',
          'The page is not on your navigation.',
        ],
        steps: [
          'Open Agent Status. Search by name or email, or filter User Status.',
          'If you are an assigner, change User Status. Assigned Tickets is the total load. Active Tickets counts open-status tickets only.',
          'Use this list when Incoming Request’s Available Agents panel is not enough. View All on that panel also opens Agent Status.',
        ],
        screenshots: [
          {
            src: H('ag-01-agent-status.png'),
            alt: 'Agent Status table',
            caption: 'Agent Status: name, User Status (Online / Break / Lunch / Offline), email, role, assigned and active tickets.',
          },
        ],
        manualLinks: [{ label: 'Incoming Request → Stats overview and Available Agents' }],
      },
      {
        question: 'My task is not on My Tasks',
        symptom: 'A task you expect is missing, or View does not open it.',
        causes: [
          'My Tasks lists only tasks currently assigned to you, across tickets. Team or unassigned work is not included.',
          'The summary cards (Total, Open, Overdue, Due Today) are your scope, not the company totals on Task Reports.',
        ],
        steps: [
          'Confirm you are the assignee on the ticket’s Tasks tab.',
          'Use View or the parent ticket number. Both open /tickets/{id}/view?task={taskId} and the task drawer.',
          'Complete, skip, or reassign from that drawer. My Tasks does not complete the task by itself.',
        ],
        screenshots: [
          {
            src: H('myt-01-my-tasks.png'),
            alt: 'My Tasks list',
            caption: 'My Tasks: Total / Open / Overdue / Due Today, then task number, parent ticket, status, and View.',
          },
        ],
        manualLinks: [{ label: 'Tickets → Tasks tab — list, create, templates, and task drawer' }],
      },
      {
        question: 'Mailbox reply did not create or update a ticket',
        symptom: 'You replied in Mailbox Inbox and expected a ticket or an Incoming Request change.',
        causes: [
          'Mailbox Inbox is the ingested Microsoft Graph mailbox. Reply, Reply all, and Forward send mail. They do not acknowledge a request or close a ticket.',
          'Case Threads is the case-centered place to reply on a Ticket Request. Intake / acknowledge still run through Case Threads queues and Incoming Request.',
        ],
        steps: [
          'For case work, open Case Threads (sidebar or header mail icon), select the client/requestor and Ticket Request, then Reply from the thread.',
          'If the message is not on a request yet, clear Unlinked / Needs review with Create, Attach, or Ignore.',
          'To create the parent ticket, acknowledge on Incoming Request. Use Mailbox Inbox only when you need the flat Emails / Threads reader (Reply all / Forward).',
        ],
        screenshots: [
          {
            src: H('ct-01-overview.png'),
            alt: 'Case Threads for case replies',
            caption: 'Prefer Case Threads for replies that stay on a Ticket Request.',
          },
          {
            src: H('mbx-01-inbox.png'),
            alt: 'Mailbox inbox and reading pane',
            caption: 'Mailbox Inbox: Emails / Threads, Unread only, search, Reply / Reply all / Forward, Mark as read.',
          },
        ],
        manualLinks: [
          { label: 'Case Threads → How Case Threads fits Incoming Request and Mailbox Inbox' },
          { label: 'Incoming Request → Compose Email modal' },
        ],
      },
    ],
  },
  {
    title: 'Reports',
    intro: 'Period reports. Empty From / To means the last 30 days. Download is CSV, Excel, or PDF on most reports. Task Reports does not download.',
    items: [
      {
        question: 'Report numbers do not match Operations home or Incoming Request',
        symptom: 'Created, Closed, or SLA % on Reports Overview disagree with the dashboard or the intake stats.',
        causes: [
          'Reports Overview is period analytics (default last 30 days, or the From / To you set). Dashboard and Incoming stats are current operational snapshots.',
          'Incoming From / To filter the intake table only. They do not drive this report.',
        ],
        steps: [
          'Read “Showing data for” under the date fields before comparing counts.',
          'Created, Closed, Net change, Overdue (open), SLA compliance, and Avg first response are for that period.',
          'Ticket flow is created vs closed per day. Status distribution and Source channel explain mix. Detailed report cards below the charts open the full pages.',
        ],
        screenshots: [
          {
            src: H('rpt-01-overview.png'),
            alt: 'Reports overview',
            caption: 'Reports Overview: date range, period cards, ticket flow, status, and source. Download is at the top right.',
          },
        ],
      },
      {
        question: 'Task report is empty or the download button does nothing',
        symptom: 'Open / Overdue tabs show 0, or Download on Task Reports does not export.',
        causes: [
          'Group by changes how Open, Overdue, and completion rows are bucketed (Module, Assignee, Team, Parent ticket).',
          'Task Reports has export turned off. Other reports use the format menu and Download.',
        ],
        steps: [
          'Set From / To, then Group open / overdue / completion by the dimension you need.',
          'Use the tabs: Open, Overdue, By user, By module, SLA compliance, Completion time, Task completion.',
          'Export from Agent Performance, SLA Compliance, FE Attendance, or Reports Overview instead of Task Reports.',
        ],
        screenshots: [
          {
            src: H('rpt-02-task-reports.png'),
            alt: 'Task reports open tab',
            caption: 'Task Reports Open tab: total open tasks and a group table. Download is disabled on this page.',
          },
        ],
      },
      {
        question: 'SLA compliance or agent leaderboard looks wrong',
        symptom: 'An agent’s SLA % or a ticket breach reason does not match what you see on the ticket.',
        causes: [
          'Agent Performance is a period leaderboard (open, resolved, average first response, SLA %, breached, load).',
          'SLA Compliance → Tickets is parent resolution SLA. The Tasks tab is task SLA. They are not the same clock as the Incoming Request ack timer.',
        ],
        steps: [
          'Set the same From / To on both pages before comparing.',
          'Agent Performance: Agent, Team, Open, Resolved, Avg FRT, SLA %, Breached, Load.',
          'SLA Compliance Tickets: Within SLA %, Breached, Avg resolution (hrs), Tickets with SLA, then ticket number, due by, resolved, status, and reason. Switch to Tasks for task SLA.',
        ],
        screenshots: [
          {
            src: H('rpt-03-agent-performance.png'),
            alt: 'Agent performance leaderboard',
            caption: 'Agent Performance leaderboard and CSV / Excel / PDF Download.',
          },
          {
            src: H('rpt-04-sla.png'),
            alt: 'SLA compliance tickets tab',
            caption: 'SLA Compliance Tickets tab: within-SLA cards and the breach table. Tasks is a separate tab.',
          },
        ],
      },
      {
        question: 'FE attendance export or punch rows are missing',
        symptom: 'A field engineer’s punch-in does not appear, or Export CSV is empty.',
        causes: [
          'FE User ID filters to one engineer. Blank means all FEs.',
          'The report date range is last 30 days unless From / To are set. An open shift has punch-in and no punch-out.',
        ],
        steps: [
          'Set From / To, optionally FE User ID, and read Punch In, Punch Out, Hours, and Status.',
          'Use Export CSV on the page for the filtered rows, or the header Download for the report file.',
          'Attendance is punch compliance. Visit schedule is on FE Schedule, not this report.',
        ],
        screenshots: [
          {
            src: H('rpt-05-fe-attendance.png'),
            alt: 'FE attendance report',
            caption: 'FE Attendance: date range, FE User ID, Export CSV, punch in/out, hours, status.',
          },
        ],
      },
    ],
  },
  {
    title: 'Field engineers and profile',
    intro: 'Dispatch calendar, live location, leave approval, and your own account. Visit completion still happens on the mobile app.',
    items: [
      {
        question: 'An FE visit is not on the schedule calendar',
        symptom: 'You created an FE Visit but the engineer’s day is empty.',
        causes: [
          'From / To does not cover the visit date. The calendar shows the month that range lands in.',
          'FE User ID or Dispatch status is filtering the row out.',
          'Ready dispatch without a saved schedule does not place a calendar block.',
        ],
        steps: [
          'Open FE Schedule. Widen From / To, clear FE User ID, set Dispatch status to All statuses, then Apply.',
          'Use Calendar for the month grid or List for a table. A block shows the time and engineer name.',
          'Manage the visit from the ticket (dispatch and the Field Visit task). The calendar does not complete the visit.',
        ],
        screenshots: [
          {
            src: H('fe-01-schedule.png'),
            alt: 'FE schedule calendar',
            caption: 'FE Schedule: From / To, FE User ID, Dispatch status, Calendar or List.',
          },
        ],
        manualLinks: [{ label: 'Tickets → Field engineer dispatch and submit actions' }],
      },
      {
        question: 'Leave request will not approve, or the engineer still shows on the map',
        symptom: 'Approve / Reject does nothing useful, or FE Live Map still shows someone who is off.',
        causes: [
          'Time Off defaults the Status filter to Pending. Approved and rejected rows are hidden until you change it.',
          'Live map shows recent location pings. It does not remove an engineer because leave was approved.',
        ],
        steps: [
          'On FE Time Off, set Status, optional FE User ID and dates, then Apply. Pending rows have Approve and Reject.',
          'On FE Live Map, set FE User ID and Since (minutes). Check Online only → Show online to hide people with no recent device ping, then Apply.',
          'Approved leave does not cancel an existing FE Visit. Reassign or cancel the visit on the ticket.',
        ],
        screenshots: [
          {
            src: H('fe-02-time-off.png'),
            alt: 'FE time off queue',
            caption: 'FE Time Off: Status filter, dates, Approve and Reject on pending leave.',
          },
          {
            src: H('fe-03-live-map.png'),
            alt: 'FE live map',
            caption: 'FE Live Map: recent pings and device status. Filters limit who appears.',
          },
        ],
      },
      {
        question: 'I cannot change my email, username, or turn off 2FA',
        symptom: 'Email or username is locked, or Disable 2FA is unavailable.',
        causes: [
          'Email Address and Username are account identifiers and stay read-only on Profile.',
          'If two-factor is required system-wide, you cannot disable it. Enable 2FA is also hidden when the system has not turned 2FA on.',
        ],
        steps: [
          'Update First Name, Last Name, Nickname, and Biography, then save. Use Generate Password only when you intend to replace your password.',
          'Under Two-Factor Authentication, use Enable 2FA and store the backup codes. Generate Backup Codes again if you lose them.',
          'Ask an administrator for an email or username change, or to change the system-wide 2FA requirement.',
        ],
        screenshots: [
          {
            src: H('prf-01-profile.png'),
            alt: 'Profile page',
            caption: 'Profile: personal information, read-only email and username, password, and two-factor authentication.',
          },
        ],
      },
    ],
  },
  {
    title: 'Service Catalog',
    intro:
      'Use this section when catalog menus are missing, codes cannot be edited, or ticket/task dropdowns do not show new values.',
    items: [
      {
        question: 'Service Catalog menu is missing or a child page is empty',
        symptom: 'No Service Catalog group in the sidebar, or Create buttons are hidden.',
        causes: [
          'Your role lacks can_view / can_create on the catalog navigation routes.',
          'You are on the wrong environment or the navigation was not seeded for your role.',
        ],
        steps: [
          'Confirm with an admin that your role includes Service Catalog children (Service Types, Form Options, etc.).',
          'Reload after a role change so user_routes refresh.',
          'Open User Manual → Service Catalog for the intended page path.',
        ],
        screenshots: [
          {
            src: H('sc-01-service-types-list.png'),
            alt: 'Service Types list',
            caption: 'Example catalog list with Create when permissions allow.',
          },
        ],
        manualLinks: [{ label: 'Service Catalog → Shared list workflow (All / Trash)' }],
      },
      {
        question: 'Cannot change Code after creating a catalog record',
        symptom: 'Code field is disabled on Edit for Service Type, Ticket Status, Task Status, or Option List group.',
        causes: [
          'Codes are immutable after create so historical tickets/tasks keep stable submitted values.',
        ],
        steps: [
          'Create a new record with the correct code if the old one is wrong.',
          'Deactivate (Active = off) or trash the obsolete record instead of rewriting its code.',
          'For Form Options group code: it cannot be changed later — create a new list if needed.',
        ],
        screenshots: [
          {
            src: H('sc-02-service-types-create.png'),
            alt: 'Create Service Type',
            caption: 'Set Code carefully on create — it locks on edit.',
          },
        ],
        manualLinks: [{ label: 'Service Catalog → Service Types' }],
      },
      {
        question: 'New severity / hold reason / SLA option not showing on tickets',
        symptom: 'Form Options value was added but ticket form dropdown is unchanged.',
        causes: [
          'Value is inactive or still in Trash.',
          'Wrong option list selected (e.g. edited billing_type instead of severity).',
          'Browser cache / session still using old options.',
        ],
        steps: [
          'Open Form Options → select the correct list → confirm the value is Active on All (not Trash).',
          'Hard-refresh the ticket create/edit page.',
          'For SLA Applies To / hold reasons, confirm the group_code matches what the form loads.',
        ],
        screenshots: [
          {
            src: H('sc-15-form-options.png'),
            alt: 'Form Options',
            caption: 'Select the option list, then manage Active values.',
          },
        ],
        manualLinks: [{ label: 'Service Catalog → Form Options (Ticket Option Lists)' }],
      },
      {
        question: 'Apply template / Create task from template has no templates',
        symptom: 'Create task → From template shows No standalone templates available.',
        causes: [
          'Only multi-step sequential workflows exist; standalone templates need zero steps or non-sequential.',
          'Template is inactive or in trash.',
        ],
        steps: [
          'In Task Templates, create a standalone template (no workflow steps) for Create task → From template.',
          'Use Apply template on the ticket for multi-step Sequential workflows.',
          'Ensure the template is Active.',
        ],
        screenshots: [
          {
            src: H('sc-10-task-templates.png'),
            alt: 'Task Templates',
            caption: 'Standalone vs sequential templates for Create vs Apply.',
          },
        ],
        manualLinks: [{ label: 'Service Catalog → Task Templates' }],
      },
      {
        question: 'Closed or On hold flag on a ticket status does not behave as expected',
        symptom: 'Choosing the status does not close the ticket, or hold time does not pause.',
        causes: [
          'Closed and On hold are flags on Parent Ticket Statuses. The code is locked after create.',
          'Close-family behavior (open-task warning, CSAT) follows status codes such as closed and resolved_incomplete.',
        ],
        steps: [
          'Open Parent Ticket Statuses and confirm Closed / On hold on the row you expect.',
          'Create a new status if the code was wrong. Do not try to edit the code later.',
          'On the ticket, change Status on Properties. Open tasks warn you before the parent closes.',
        ],
        screenshots: [
          {
            src: H('sc-03-parent-statuses.png'),
            alt: 'Ticket statuses list',
            caption: 'Ticket Statuses — Code, Label, Closed, On hold.',
          },
          {
            src: H('sc-03b-parent-status-create.png'),
            alt: 'Create ticket status',
            caption: 'Create Ticket Status: set Closed and On hold before the code locks.',
          },
        ],
        manualLinks: [{ label: 'Service Catalog → Parent Ticket Statuses' }],
      },
      {
        question: 'SLA on the ticket does not match the catalog policy',
        symptom: 'A new SLA row exists but tickets still show no target or the old minutes.',
        causes: [
          'The policy is inactive, applies to the wrong entity, or the ticket was already submitted under the previous policy.',
          'Form Options group SLA Applies To does not match the policy.',
        ],
        steps: [
          'Open SLA Policies and confirm the row is active and the minutes and applies-to values are the ones you intend.',
          'Check Form Options → SLA Applies To if the dropdown on the SLA form is missing the target.',
          'New tickets pick up the policy. Open tickets keep the snapshot they were submitted with.',
        ],
        screenshots: [
          {
            src: H('sc-06-sla-list.png'),
            alt: 'SLA policies list',
            caption: 'SLA list — confirm the active policy before expecting ticket timers to change.',
          },
          {
            src: H('sc-07-sla-create.png'),
            alt: 'Create SLA',
            caption: 'Create SLA — minutes and what the policy applies to.',
          },
        ],
        manualLinks: [{ label: 'Service Catalog → SLA Policies' }],
      },
      {
        question: 'Issue category, checklist, or tool/part is missing on the ticket form',
        symptom: 'The cascade, checklist attach list, or tool name search does not show a catalog row you added.',
        causes: [
          'The row is inactive or in Trash.',
          'Issue categories are a parent/child cascade. A child will not appear until its parent is selected.',
          'FE checklists and checklist templates are separate catalogs.',
        ],
        steps: [
          'Issue Categories: confirm Name, parent, and Active, then pick the parent on the ticket before the child.',
          'Checklist Templates feed task template steps and the task Checklist tab. FE Checklist Builder feeds field-visit checklists.',
          'Tool / Part Catalog: the name must be Active to show in the tools/parts autocomplete.',
        ],
        screenshots: [
          {
            src: H('sc-14b-issue-category-create.png'),
            alt: 'Create issue category',
            caption: 'Issue category form — Name, parent category, Active.',
          },
          {
            src: H('sc-13b-checklist-template-modal.png'),
            alt: 'Checklist template modal',
            caption: 'Checklist template — Name, Active, and required items.',
          },
          {
            src: H('sc-18b-fe-checklist-modal.png'),
            alt: 'FE checklist modal',
            caption: 'FE Checklist Builder uses its own Create Checklist modal.',
          },
          {
            src: H('sc-17b-tool-part-modal.png'),
            alt: 'Add tool or part',
            caption: 'Tool / Part must stay Active to appear on the ticket form.',
          },
        ],
        manualLinks: [
          { label: 'Service Catalog → Issue Categories' },
          { label: 'Service Catalog → Checklist Templates' },
        ],
      },
    ],
  },
]

export default function FaqPage() {
  const { textSecondary, border } = useThemeColors()

  return (
    <HelpPageLayout>
      <VStack align="stretch" spacing={6} w="full" maxW="1200px">
        <Box>
          <Heading size="lg" mb={2}>
            FAQ — Troubleshooting
          </Heading>
          <Text color={textSecondary}>
            Symptom → cause → fix steps for Operations home, Case Threads, Incoming Request, Tickets, mailbox, agent
            status, reports, field engineers, profile, service catalog, and the email-to-close path. For field-by-field
            how-to guidance, see the{' '}
            <ChakraLink as={RouterLink} to="/user-manual" color="blue.500">
              User Manual
            </ChakraLink>
            .
          </Text>
        </Box>

        {faqCategories.map((category) => (
          <Box key={category.title}>
            <Heading size="md" mb={category.intro ? 2 : 4}>
              {category.title}
            </Heading>
            {category.intro && (
              <Text color={textSecondary} mb={4} fontSize="sm">
                {category.intro}
              </Text>
            )}
            <Accordion allowMultiple reduceMotion>
              {category.items.map((item) => (
                <AccordionItem key={item.question} borderColor={border}>
                  <h3>
                    <AccordionButton py={3} px={2}>
                      <Box as="span" flex="1" textAlign="left" fontWeight="semibold">
                        {item.question}
                      </Box>
                      <AccordionIcon />
                    </AccordionButton>
                  </h3>
                  <AccordionPanel pb={4} px={2}>
                    <TroubleshootStep
                      symptom={item.symptom}
                      causes={item.causes}
                      steps={item.steps}
                      screenshots={item.screenshots}
                      manualLinks={item.manualLinks}
                      border={border}
                      textSecondary={textSecondary}
                    />
                  </AccordionPanel>
                </AccordionItem>
              ))}
            </Accordion>
            <Divider mt={6} borderColor={border} />
          </Box>
        ))}
      </VStack>
    </HelpPageLayout>
  )
}
